import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { errorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  ArrowLeft,
  Loader2,
  Send,
  History,
  Trash2,
  Bell,
  Eye,
  EyeOff,
  Copy,
  KeyRound,
  Save,
  Check,
  Plus,
  Zap,
  Receipt,
  Pencil,
  CalendarIcon,
  Upload,
  Download,
  FileText,
  FileWarning,
} from "lucide-react";
// Aliased: this file already has a local CATEGORIES for the service catalogue.
import {
  STATUSES,
  PRIORITIES,
  CATEGORIES as TASK_CATEGORIES,
  emptyTaskForm,
} from "@/constants/tasks";
import { formatDate, formatDateTime, relTime } from "@/lib/format";
import { toast } from "sonner";
import HistoryDialog from "@/components/HistoryDialog";
import ReasonDialog from "@/components/ReasonDialog";
import ClientFormDialog from "@/components/ClientFormDialog";
import TaskDetailDialog from "@/components/TaskDetailDialog";
import InvoiceDetailDialog, {
  STATUS_TONE as INVOICE_STATUS_TONE,
  money,
} from "@/components/InvoiceDetailDialog";
import { useAuth, atLeast } from "@/lib/auth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const ROC_APPLICABLE = ["llp", "private_limited", "public_limited"];

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [docs, setDocs] = useState([]);
  const [activity, setActivity] = useState([]);
  const [outstanding, setOutstanding] = useState(null);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reminding, setReminding] = useState(false);
  // Per-client task creation — stays on this page, client is pinned to this client.
  const [users, setUsers] = useState([]);
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [taskForm, setTaskForm] = useState(emptyTaskForm);
  const [taskBusy, setTaskBusy] = useState(false);
  const [taskDateOpen, setTaskDateOpen] = useState(false);
  const [openTaskId, setOpenTaskId] = useState(null);
  // Per-client documents — upload / track-pending / delete, all in place.
  const fileInputRef = useRef(null);
  const fulfilInputRef = useRef(null);
  const fulfilDocRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [pendingForm, setPendingForm] = useState({ name: "", description: "" });
  const [pendingBusy, setPendingBusy] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [openInvoiceId, setOpenInvoiceId] = useState(null);
  const canArchive = atLeast(user, "partner");
  const canEdit = atLeast(user, "article_assistant");
  const canRemind = atLeast(user, "manager");
  const canEditCreds = atLeast(user, "manager");
  const canManageServices = atLeast(user, "manager");

  const load = useCallback(async () => {
    const c = await api.get(`/clients/${clientId}`);
    setClient(c.data);
    const [t, d, a, o, inv] = await Promise.all([
      api.get("/tasks", { params: { client_id: clientId } }),
      api.get("/documents", { params: { client_id: clientId } }),
      api.get("/activity", {
        params: { entity_type: "client", entity_id: clientId },
      }),
      api.get(`/clients/${clientId}/outstanding`).catch(() => ({ data: null })),
      api
        .get("/invoices", { params: { client_id: clientId } })
        .catch(() => ({ data: [] })),
    ]);
    setTasks(t.data);
    setDocs(d.data);
    setActivity(a.data);
    setOutstanding(o.data);
    setInvoices(inv.data);
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  // Assignee options for the new-task dialog. Loaded once, not per client.
  useEffect(() => {
    api
      .get("/users")
      .then(({ data }) => setUsers(data))
      .catch(() => {});
  }, []);

  const openNewTask = () => {
    setTaskForm(emptyTaskForm());
    setNewTaskOpen(true);
  };

  // --- Documents (all actions refresh in place, never navigate) ---

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("client_id", clientId);
        await api.post("/documents/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(
        files.length > 1
          ? `Uploaded ${files.length} documents`
          : "Document uploaded",
      );
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const createPendingDoc = async () => {
    if (!pendingForm.name.trim()) return;
    setPendingBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", pendingForm.name.trim());
      fd.append("client_id", clientId);
      if (pendingForm.description)
        fd.append("description", pendingForm.description);
      await api.post("/documents/pending", fd);
      toast.success("Pending document tracked");
      setPendingOpen(false);
      setPendingForm({ name: "", description: "" });
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Could not track document"));
    } finally {
      setPendingBusy(false);
    }
  };

  const pickFulfilFile = (doc) => {
    fulfilDocRef.current = doc;
    fulfilInputRef.current?.click();
  };

  const fulfilPendingDoc = async (file) => {
    const doc = fulfilDocRef.current;
    if (!file || !doc) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/documents/${doc.id}/version`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`"${doc.name}" received`);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Upload failed"));
    } finally {
      setUploading(false);
      fulfilDocRef.current = null;
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const deleteDoc = async (reason) => {
    if (!docToDelete) return;
    try {
      await api.delete(`/documents/${docToDelete.id}`, {
        headers: { "X-Change-Reason": reason },
      });
      toast.success("Document deleted");
      setDocToDelete(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to delete document"));
    }
  };

  const createTask = async () => {
    if (!taskForm.title.trim()) return;
    setTaskBusy(true);
    try {
      await api.post("/tasks", {
        title: taskForm.title,
        description: taskForm.description || null,
        client_id: clientId,
        assignee_id:
          taskForm.assignee_id === "none" ? null : taskForm.assignee_id,
        priority: taskForm.priority,
        task_status: taskForm.task_status,
        category: taskForm.category === "none" ? null : taskForm.category,
        due_date: taskForm.due_date ? taskForm.due_date.toISOString() : null,
      });
      toast.success("Task created");
      setNewTaskOpen(false);
      setTaskForm(emptyTaskForm());
      // Refresh in place — deliberately no navigate() so the user keeps their
      // spot on the client record.
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to create task"));
    } finally {
      setTaskBusy(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await api.post(`/clients/${clientId}/notes`, { content: note });
      setNote("");
      toast.success("Note added");
      load();
    } finally {
      setSavingNote(false);
    }
  };

  const doArchive = async (reason) => {
    await api.delete(`/clients/${clientId}`, {
      headers: { "X-Change-Reason": reason },
    });
    toast.success("Client archived");
    navigate("/clients");
  };

  const sendReminder = async () => {
    setReminding(true);
    try {
      const { data } = await api.post(
        `/clients/${clientId}/send-payment-reminder`,
      );
      toast.success(
        `Reminder sent — ₹${data.outstanding.toLocaleString("en-IN")}`,
      );
    } catch (e) {
      toast.error(errorMessage(e, "Failed to send reminder"));
    } finally {
      setReminding(false);
    }
  };

  if (!client) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="animate-spin mr-2" size={16} /> Loading…
      </div>
    );
  }

  const hasBalance = outstanding && outstanding.outstanding > 0;

  return (
    <div className="space-y-4" data-testid="client-detail-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate("/clients")}>
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
        <div className="flex-1" />
        {canRemind && hasBalance && (
          <Button
            variant="outline"
            size="sm"
            onClick={sendReminder}
            disabled={reminding || !client.email}
            data-testid="send-reminder-btn"
            className="text-brand-gold border-amber-300 dark:border-amber-800"
          >
            {reminding ? (
              <Loader2 size={13} className="animate-spin mr-1.5" />
            ) : (
              <Bell size={13} className="mr-1.5" />
            )}
            Send payment reminder
          </Button>
        )}
        {canEdit && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditOpen(true)}
            data-testid="client-edit-btn"
          >
            <Pencil size={13} className="mr-1.5" /> Edit
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setHistoryOpen(true)}
          data-testid="client-history-btn"
        >
          <History size={13} className="mr-1.5" /> History
        </Button>
        {canArchive && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeleteOpen(true)}
            data-testid="client-archive-btn"
            className="text-red-600 hover:text-red-700 border-red-200 dark:border-red-900"
          >
            <Trash2 size={13} className="mr-1.5" /> Archive
          </Button>
        )}
      </div>

      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-mono text-muted-foreground">
            {client.client_code}
          </div>
          <h2 className="font-heading text-2xl font-semibold tracking-tight mt-0.5">
            {client.name}
          </h2>
          <div className="text-sm text-muted-foreground mt-1 capitalize">
            {client.constitution.replace(/_/g, " ")}
          </div>
        </div>
        <div className="flex gap-1.5 flex-wrap max-w-md justify-end">
          {(client.services_availed || []).map((s) => (
            <Badge key={s} variant="secondary" className="text-[10px]">
              {s}
            </Badge>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Info label="PAN" value={client.pan} mono />
        <Info label="GSTIN" value={client.gstin} mono />
        <Info label="Email" value={client.email} />
        <Info label="Mobile" value={client.mobile || client.phone} />
        <Info
          label="Outstanding"
          value={
            outstanding
              ? `₹${outstanding.outstanding.toLocaleString("en-IN", { minimumFractionDigits: 0 })}`
              : "—"
          }
          mono
          tone={hasBalance ? "danger" : undefined}
          testid="outstanding-info"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Info label="Address" value={client.address} testid="address-info" />
        <Info
          label="City / State"
          value={[client.city, client.state].filter(Boolean).join(", ")}
          testid="city-state-info"
        />
        <Info
          label="Pincode"
          value={client.pincode}
          mono
          testid="pincode-info"
        />
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">
            Overview
          </TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services">
            Services
          </TabsTrigger>
          <TabsTrigger value="credentials" data-testid="tab-credentials">
            Credentials
          </TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">
            Tasks ({tasks.length})
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">
            Documents ({docs.length})
          </TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">
            Invoices ({invoices.length})
          </TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">
            Timeline
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card className="p-4 border-border shadow-none">
            <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
              Add note
            </h3>
            <Textarea
              placeholder="Write a note about this client…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="client-note-input"
              rows={2}
            />
            <div className="mt-2 flex justify-end">
              <Button
                size="sm"
                onClick={addNote}
                disabled={!note.trim() || savingNote}
                data-testid="client-note-submit"
              >
                {savingNote ? (
                  <Loader2 size={12} className="animate-spin mr-1.5" />
                ) : (
                  <Send size={12} className="mr-1.5" />
                )}
                Add note
              </Button>
            </div>
          </Card>

          <div className="space-y-2">
            {(client.notes || [])
              .slice()
              .reverse()
              .map((n) => (
                <Card key={n.id} className="p-3 border-border shadow-none">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium">{n.author_name}</span>
                    <span className="text-muted-foreground font-mono">
                      {relTime(n.created_at)}
                    </span>
                  </div>
                  <div className="text-sm mt-1.5 whitespace-pre-wrap">
                    {n.content}
                  </div>
                </Card>
              ))}
            {(!client.notes || client.notes.length === 0) && (
              <div className="text-sm text-muted-foreground text-center py-6">
                No notes yet.
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="credentials">
          <CredentialsPanel
            client={client}
            canEdit={canEditCreds}
            onSaved={load}
          />
        </TabsContent>

        <TabsContent value="services">
          <ServicesPanel
            client={client}
            canManage={canManageServices}
            onInvoiceChange={load}
          />
        </TabsContent>

        <TabsContent value="tasks">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs text-muted-foreground">
              {tasks.length} {tasks.length === 1 ? "task" : "tasks"} for this
              client
            </div>
            <Button
              size="sm"
              onClick={openNewTask}
              data-testid="client-new-task"
            >
              <Plus size={13} className="mr-1.5" /> New task
            </Button>
          </div>
          <div className="border border-border rounded-md">
            {tasks.length === 0 && (
              <div className="p-6 text-sm text-muted-foreground text-center">
                No tasks for this client.{" "}
                <button
                  onClick={openNewTask}
                  className="underline hover:text-foreground"
                  data-testid="client-new-task-empty"
                >
                  Create one
                </button>
                .
              </div>
            )}
            {tasks.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setOpenTaskId(t.id)}
                className="w-full text-left flex items-center gap-3 px-3 py-2.5 border-b border-border last:border-0 hover:bg-muted/40"
                data-testid={`client-task-row-${t.id}`}
              >
                <span className="text-xs font-mono uppercase text-muted-foreground w-24">
                  {t.task_status.replace("_", " ")}
                </span>
                <span className="text-sm font-medium flex-1 truncate">
                  {t.title}
                </span>
                {t.due_date && (
                  <span className="text-xs font-mono text-muted-foreground">
                    {formatDate(t.due_date, "dd MMM")}
                  </span>
                )}
              </button>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="documents">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs text-muted-foreground">
              {docs.length} {docs.length === 1 ? "document" : "documents"} for
              this client
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPendingOpen(true)}
                data-testid="client-track-pending"
              >
                <Plus size={13} className="mr-1.5" /> Track pending
              </Button>
              <Button
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="client-upload-doc"
              >
                {uploading ? (
                  <Loader2 size={13} className="animate-spin mr-1.5" />
                ) : (
                  <Upload size={13} className="mr-1.5" />
                )}
                Upload
              </Button>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              uploadFiles(e.target.files);
              e.target.value = "";
            }}
            data-testid="client-doc-file-input"
          />
          <input
            ref={fulfilInputRef}
            type="file"
            className="hidden"
            onChange={(e) => {
              fulfilPendingDoc(e.target.files?.[0]);
              e.target.value = "";
            }}
            data-testid="client-doc-fulfil-input"
          />
          <div className="border border-border rounded-md">
            {docs.length === 0 && (
              <div className="p-6 text-sm text-muted-foreground text-center">
                No documents yet.{" "}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="underline hover:text-foreground"
                  data-testid="client-upload-doc-empty"
                >
                  Upload one
                </button>
                .
              </div>
            )}
            {docs.map((d) => (
              <div
                key={d.id}
                className="flex items-center gap-3 px-3 py-2.5 border-b border-border last:border-0 hover:bg-muted/40 group"
              >
                {d.is_pending ? (
                  <FileWarning size={13} className="text-amber-500 shrink-0" />
                ) : (
                  <FileText
                    size={13}
                    className="text-muted-foreground shrink-0"
                  />
                )}
                <span className="text-sm font-medium flex-1 truncate">
                  {d.name}
                </span>
                {d.is_pending ? (
                  <Badge
                    variant="outline"
                    className="text-[10px] text-amber-600 border-amber-500/50"
                  >
                    Pending
                  </Badge>
                ) : (
                  <span className="text-xs font-mono text-muted-foreground">
                    v{d.current_version}
                  </span>
                )}
                <div className="flex items-center gap-1">
                  {d.is_pending && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => pickFulfilFile(d)}
                      disabled={uploading}
                      title="Upload the received file"
                      data-testid={`client-doc-fulfil-${d.id}`}
                    >
                      <Upload size={13} />
                    </Button>
                  )}
                  {!d.is_pending && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => downloadDoc(d)}
                      title="Download"
                      data-testid={`client-doc-download-${d.id}`}
                    >
                      <Download size={13} />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-red-600 hover:text-red-600"
                    onClick={() => setDocToDelete(d)}
                    title="Delete"
                    data-testid={`client-doc-delete-${d.id}`}
                  >
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="invoices">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs text-muted-foreground">
              {invoices.length} {invoices.length === 1 ? "invoice" : "invoices"}{" "}
              for this client
            </div>
            <div className="text-xs text-muted-foreground">
              Click an invoice to view lines, issue it, record a payment or
              download the PDF.
            </div>
          </div>
          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium text-xs text-muted-foreground">
                    Invoice #
                  </th>
                  <th className="px-3 py-2 font-medium text-xs text-muted-foreground">
                    Date
                  </th>
                  <th className="px-3 py-2 font-medium text-xs text-muted-foreground">
                    Due
                  </th>
                  <th className="px-3 py-2 font-medium text-xs text-muted-foreground text-right">
                    Amount
                  </th>
                  <th className="px-3 py-2 font-medium text-xs text-muted-foreground text-right">
                    Outstanding
                  </th>
                  <th className="px-3 py-2 font-medium text-xs text-muted-foreground">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-10 text-center text-sm text-muted-foreground"
                    >
                      <Receipt
                        className="inline mr-2 mb-1 opacity-40"
                        size={16}
                      />
                      No invoices for this client yet.
                    </td>
                  </tr>
                )}
                {invoices.map((inv) => (
                  <tr
                    key={inv.id}
                    onClick={() => setOpenInvoiceId(inv.id)}
                    className="border-b border-border last:border-0 hover:bg-muted/40 cursor-pointer"
                    data-testid={`client-invoice-row-${inv.id}`}
                  >
                    <td className="px-3 py-2.5 font-mono text-xs font-medium">
                      {inv.invoice_number}
                    </td>
                    <td className="px-3 py-2.5 text-xs font-mono text-muted-foreground">
                      {formatDate(inv.invoice_date, "dd MMM yyyy")}
                    </td>
                    <td className="px-3 py-2.5 text-xs font-mono text-muted-foreground">
                      {inv.due_date
                        ? formatDate(inv.due_date, "dd MMM yyyy")
                        : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {money(inv.grand_total)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {money(inv.amount_due)}
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge
                        variant="outline"
                        className={`text-[10px] capitalize ${INVOICE_STATUS_TONE[inv.invoice_status] || ""}`}
                      >
                        {String(inv.invoice_status).replace("_", " ")}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="timeline">
          <div className="space-y-2">
            {activity.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-6">
                No activity for this client.
              </div>
            )}
            {activity.map((a) => (
              <div
                key={a.id}
                className="text-sm py-1.5 border-b border-border/60 flex items-center gap-3"
              >
                <span className="font-medium">{a.actor_name}</span>
                <span className="text-muted-foreground">{a.action}</span>
                {a.detail && (
                  <span className="text-muted-foreground italic truncate">
                    &ldquo;{a.detail}&rdquo;
                  </span>
                )}
                <span className="ml-auto text-xs text-muted-foreground font-mono">
                  {formatDateTime(a.created_at)}
                </span>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <ClientFormDialog
        client={client}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={load}
      />
      <HistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        entityType="client"
        entityId={clientId}
        entityLabel={client.name}
      />
      <ReasonDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onConfirm={doArchive}
        title="Archive client"
        description="Archiving is a soft-delete. The client and its data are retained in audit logs. This action requires a reason."
        confirmLabel="Archive client"
      />

      {/* Document soft-delete — regulated, needs an audit reason. */}
      <ReasonDialog
        open={!!docToDelete}
        onOpenChange={(v) => !v && setDocToDelete(null)}
        onConfirm={deleteDoc}
        title="Delete document"
        description={`"${docToDelete?.name ?? ""}" will be soft-deleted and retained in audit logs. This action requires a reason.`}
        confirmLabel="Delete document"
      />

      {/* Track a document you're still waiting on from this client. */}
      <Dialog open={pendingOpen} onOpenChange={setPendingOpen}>
        <DialogContent className="max-w-md" data-testid="client-pending-dialog">
          <DialogHeader>
            <DialogTitle>Track a pending document</DialogTitle>
            <DialogDescription>
              Record a document you are still waiting on from {client.name}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Document name</Label>
              <Input
                autoFocus
                value={pendingForm.name}
                onChange={(e) =>
                  setPendingForm({ ...pendingForm, name: e.target.value })
                }
                placeholder="e.g. Bank statement — March 2026"
                data-testid="client-pending-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Input
                value={pendingForm.description}
                onChange={(e) =>
                  setPendingForm({
                    ...pendingForm,
                    description: e.target.value,
                  })
                }
                data-testid="client-pending-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={createPendingDoc}
              disabled={!pendingForm.name.trim() || pendingBusy}
              data-testid="client-pending-submit"
            >
              {pendingBusy && (
                <Loader2 size={13} className="animate-spin mr-1.5" />
              )}
              Track
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Same invoice modal the Invoices page uses — lines, issue, payments, PDF. */}
      {openInvoiceId && (
        <InvoiceDetailDialog
          invoiceId={openInvoiceId}
          clientName={client.name}
          onClose={() => setOpenInvoiceId(null)}
          onChange={load}
        />
      )}

      {/* Task detail opens in place — no navigation to /tasks. */}
      {openTaskId && (
        <TaskDetailDialog
          taskId={openTaskId}
          clients={[client]}
          users={users}
          onClose={() => setOpenTaskId(null)}
          onChange={load}
        />
      )}

      {/* New task — client is fixed to this record, so no client picker. */}
      <Dialog open={newTaskOpen} onOpenChange={setNewTaskOpen}>
        <DialogContent
          className="max-w-lg"
          data-testid="client-new-task-dialog"
        >
          <DialogHeader>
            <DialogTitle>New task</DialogTitle>
            <DialogDescription>
              This task will be linked to {client.name}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Title</Label>
              <Input
                autoFocus
                value={taskForm.title}
                onChange={(e) =>
                  setTaskForm({ ...taskForm, title: e.target.value })
                }
                data-testid="client-task-title"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Textarea
                value={taskForm.description}
                onChange={(e) =>
                  setTaskForm({ ...taskForm, description: e.target.value })
                }
                rows={2}
                data-testid="client-task-description"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Assignee</Label>
              <Select
                value={taskForm.assignee_id}
                onValueChange={(v) =>
                  setTaskForm({ ...taskForm, assignee_id: v })
                }
              >
                <SelectTrigger data-testid="client-task-assignee">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Unassigned —</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Priority</Label>
                <Select
                  value={taskForm.priority}
                  onValueChange={(v) =>
                    setTaskForm({ ...taskForm, priority: v })
                  }
                >
                  <SelectTrigger data-testid="client-task-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p} className="capitalize">
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Status</Label>
                <Select
                  value={taskForm.task_status}
                  onValueChange={(v) =>
                    setTaskForm({ ...taskForm, task_status: v })
                  }
                >
                  <SelectTrigger data-testid="client-task-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s.v} value={s.v}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Category</Label>
                <Select
                  value={taskForm.category}
                  onValueChange={(v) =>
                    setTaskForm({ ...taskForm, category: v })
                  }
                >
                  <SelectTrigger data-testid="client-task-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {TASK_CATEGORIES.map((c) => (
                      <SelectItem key={c.v} value={c.v}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Due date</Label>
              <Popover open={taskDateOpen} onOpenChange={setTaskDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start font-mono text-xs"
                    data-testid="client-task-due"
                  >
                    <CalendarIcon size={12} className="mr-1.5" />
                    {taskForm.due_date
                      ? formatDate(taskForm.due_date, "dd MMM yy")
                      : "Set date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={taskForm.due_date}
                    onSelect={(d) => {
                      setTaskForm({ ...taskForm, due_date: d });
                      setTaskDateOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewTaskOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={createTask}
              disabled={!taskForm.title.trim() || taskBusy}
              data-testid="client-task-submit"
            >
              {taskBusy && (
                <Loader2 size={13} className="animate-spin mr-1.5" />
              )}
              Create task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CredentialsPanel({ client, canEdit, onSaved }) {
  const showRoc = ROC_APPLICABLE.includes(client.constitution);
  const [creds, setCreds] = useState(client.credentials || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCreds(client.credentials || {});
  }, [client]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/clients/${client.id}`, { credentials: creds });
      toast.success("Credentials saved");
      onSaved?.();
    } catch (e) {
      toast.error(errorMessage(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="credentials-panel">
      <div className="text-xs text-muted-foreground flex items-center gap-2">
        <KeyRound size={12} className="text-brand-gold" />
        Portal credentials for filings. Only members of your workspace can view
        these. Stored in the same tenant as this client.
      </div>

      <Card className="p-4 border-border shadow-none space-y-4">
        <SectionHeader
          title="Income Tax"
          hint="Login is the PAN — only password stored here."
        />
        <div className="grid grid-cols-2 gap-3">
          <ReadOnlyField label="PAN (login ID)" value={client.pan} mono />
          <SecretField
            label="IT Password"
            value={creds.it_password}
            onChange={(v) => setCreds({ ...creds, it_password: v })}
            editable={canEdit}
            testid="cred-it-password"
          />
        </div>
      </Card>

      <Card className="p-4 border-border shadow-none space-y-4">
        <SectionHeader
          title="GST"
          hint="Username is auto-suggested from GSTIN if not set."
        />
        <div className="grid grid-cols-2 gap-3">
          <SecretField
            label="GST Username"
            value={creds.gst_username}
            onChange={(v) => setCreds({ ...creds, gst_username: v })}
            editable={canEdit}
            mask={false}
            testid="cred-gst-username"
          />
          <SecretField
            label="GST Password"
            value={creds.gst_password}
            onChange={(v) => setCreds({ ...creds, gst_password: v })}
            editable={canEdit}
            testid="cred-gst-password"
          />
        </div>
      </Card>

      {showRoc && (
        <Card
          className="p-4 border-border shadow-none space-y-4"
          data-testid="roc-creds"
        >
          <SectionHeader
            title="ROC / MCA"
            hint={`Required for ${client.constitution.replace(/_/g, " ")} constitution.`}
          />
          <div className="grid grid-cols-2 gap-3">
            <SecretField
              label="ROC Username"
              value={creds.roc_username}
              onChange={(v) => setCreds({ ...creds, roc_username: v })}
              editable={canEdit}
              mask={false}
              testid="cred-roc-username"
            />
            <SecretField
              label="ROC Password"
              value={creds.roc_password}
              onChange={(v) => setCreds({ ...creds, roc_password: v })}
              editable={canEdit}
              testid="cred-roc-password"
            />
          </div>
        </Card>
      )}

      <Card className="p-4 border-border shadow-none space-y-4">
        <SectionHeader
          title="TRACES (TDS)"
          hint="Optional — for TDS/TCS filings."
        />
        <div className="grid grid-cols-3 gap-3">
          <SecretField
            label="TAN"
            value={creds.tan}
            onChange={(v) => setCreds({ ...creds, tan: v })}
            editable={canEdit}
            mask={false}
            testid="cred-tan"
          />
          <SecretField
            label="TRACES Username"
            value={creds.traces_username}
            onChange={(v) => setCreds({ ...creds, traces_username: v })}
            editable={canEdit}
            mask={false}
            testid="cred-traces-username"
          />
          <SecretField
            label="TRACES Password"
            value={creds.traces_password}
            onChange={(v) => setCreds({ ...creds, traces_password: v })}
            editable={canEdit}
            testid="cred-traces-password"
          />
        </div>
      </Card>

      {canEdit && (
        <div className="flex justify-end">
          <Button
            onClick={save}
            disabled={saving}
            size="sm"
            data-testid="cred-save-btn"
          >
            {saving ? (
              <Loader2 size={13} className="animate-spin mr-1.5" />
            ) : (
              <Save size={13} className="mr-1.5" />
            )}
            Save credentials
          </Button>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ title, hint }) {
  return (
    <div>
      <div className="font-heading font-semibold text-sm">{title}</div>
      {hint && (
        <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>
      )}
    </div>
  );
}

function ReadOnlyField({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className={`text-sm mt-1 py-1.5 ${mono ? "font-mono" : ""}`}>
        {value || "—"}
      </div>
    </div>
  );
}

function SecretField({
  label,
  value,
  onChange,
  editable = false,
  mask = true,
  testid,
}) {
  const [visible, setVisible] = useState(!mask);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (e) {
      toast.error("Copy failed");
    }
  };
  const type = mask && !visible ? "password" : "text";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className="relative mt-1">
        <Input
          type={type}
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={!editable}
          className="font-mono text-xs pr-16"
          placeholder={editable ? "—" : "Not set"}
          data-testid={testid}
          autoComplete="off"
        />
        <div className="absolute inset-y-0 right-1 flex items-center gap-0.5">
          {mask && value && (
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={() => setVisible((v) => !v)}
              type="button"
              data-testid={`${testid}-toggle`}
            >
              {visible ? <EyeOff size={12} /> : <Eye size={12} />}
            </Button>
          )}
          {value && (
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={copy}
              type="button"
              data-testid={`${testid}-copy`}
            >
              {copied ? (
                <Check size={12} className="text-emerald-600" />
              ) : (
                <Copy size={12} />
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Info({ label, value, mono, tone, testid }) {
  const toneCls =
    tone === "danger" ? "text-red-600 dark:text-red-400 font-semibold" : "";
  return (
    <div className="border border-border rounded-md p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className={`text-sm mt-1 ${mono ? "font-mono" : ""} ${toneCls}`}>
        {value || "—"}
      </div>
    </div>
  );
}

const SERVICE_PRESETS = [
  { name: "Monthly GSTR-3B", category: "gst" },
  { name: "Monthly GSTR-1", category: "gst" },
  { name: "Quarterly TDS Return", category: "income_tax" },
  { name: "Income Tax Return", category: "income_tax" },
  { name: "Tax Audit u/s 44AB", category: "tax_audit" },
  { name: "Statutory Audit", category: "statutory_audit" },
  { name: "ROC Annual Filing", category: "roc" },
  { name: "Advisory / Consulting", category: "other" },
];

const CATEGORIES = [
  { v: "gst", label: "GST" },
  { v: "income_tax", label: "Income Tax" },
  { v: "roc", label: "ROC" },
  { v: "tax_audit", label: "Tax Audit" },
  { v: "statutory_audit", label: "Statutory Audit" },
  { v: "other", label: "Other" },
];

const FREQUENCIES = [
  { v: "once", label: "One-time" },
  { v: "monthly", label: "Monthly" },
  { v: "quarterly", label: "Quarterly" },
  { v: "yearly", label: "Yearly" },
];

function ServicesPanel({ client, canManage, onInvoiceChange }) {
  const [services, setServices] = useState(null);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null when creating
  const [busy, setBusy] = useState(false);
  const [billingId, setBillingId] = useState(null);
  const [draftedId, setDraftedId] = useState(null); // invoice just billed from a service row
  const emptyForm = {
    service_name: "",
    category: "gst",
    rate: "",
    frequency: "monthly",
    assignee_id: "none",
    priority: "medium",
    gst_rate: 18,
    auto_invoice: true,
  };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const { data } = await api.get(`/clients/${client.id}/services`);
    setServices(data);
  };
  useEffect(() => {
    load();
    api.get("/users").then(({ data }) => setUsers(data));
  }, [client.id]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (rule) => {
    setEditingId(rule.id);
    setForm({
      service_name: rule.service_name || rule.title || "",
      category: rule.category || "gst",
      rate: String(rule.default_bill_rate || ""),
      frequency:
        rule.recurrence === "none" ? "once" : rule.recurrence || "monthly",
      assignee_id: rule.assignee_id || "none",
      priority: rule.priority || "medium",
      gst_rate: rule.default_gst_rate ?? 18,
      auto_invoice: rule.auto_invoice ?? true,
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.service_name.trim()) return toast.error("Enter a service name");
    if (!form.rate || Number(form.rate) <= 0)
      return toast.error("Enter a positive rate");
    setBusy(true);
    try {
      const payload = {
        service_name: form.service_name.trim(),
        category: form.category,
        rate: Number(form.rate),
        frequency: form.frequency,
        assignee_id: form.assignee_id === "none" ? null : form.assignee_id,
        priority: form.priority,
        gst_rate: Number(form.gst_rate),
        auto_invoice: form.auto_invoice,
      };
      if (editingId) {
        await api.patch(`/clients/${client.id}/services/${editingId}`, payload);
        toast.success("Service updated");
      } else {
        const { data } = await api.post(
          `/clients/${client.id}/services`,
          payload,
        );
        toast.success(
          data.task_id
            ? "Service engaged · first task created"
            : "Recurring service scheduled",
        );
      }
      setOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  // Billing drafts an invoice, then hands it straight to the Issue / PDF dialog
  // so the whole bill → issue → download loop happens on this tab.
  const billNow = async (rule) => {
    setBillingId(rule.id);
    try {
      const { data } = await api.post(
        `/clients/${client.id}/services/${rule.id}/invoice-now`,
      );
      toast.success(
        `Invoice ${data.invoice_number} drafted · ₹${Number(data.grand_total).toLocaleString("en-IN")}`,
      );
      setDraftedId(data.id);
      onInvoiceChange?.();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to draft invoice"));
    } finally {
      setBillingId(null);
    }
  };

  const stop = async (rule) => {
    if (
      !window.confirm(
        `Stop service '${rule.service_name || rule.title}'? Existing tasks/invoices are retained.`,
      )
    )
      return;
    try {
      await api.delete(`/clients/${client.id}/services/${rule.id}`);
      toast.success("Service stopped");
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to stop"));
    }
  };

  const userName = (id) => users.find((u) => u.id === id)?.full_name || "—";

  return (
    <div className="space-y-4" data-testid="services-panel">
      <div className="flex items-center gap-3">
        <p className="text-sm text-muted-foreground flex-1">
          Services this client is availing — set a rate, pick a frequency. Every
          service auto-generates a draft invoice on task completion, or hit
          &ldquo;Bill now&rdquo; to invoice immediately.
        </p>
        {canManage && (
          <Button size="sm" onClick={openCreate} data-testid="new-service-btn">
            <Plus size={13} className="mr-1.5" /> Add service
          </Button>
        )}
      </div>

      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b border-border">
            <tr className="text-left">
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground">
                Service
              </th>
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground">
                Category
              </th>
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground">
                Assignee
              </th>
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground">
                Frequency
              </th>
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground text-right">
                Rate
              </th>
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground text-center">
                Auto-invoice
              </th>
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {services === null && (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-8 text-center text-muted-foreground text-sm"
                >
                  <Loader2 size={14} className="animate-spin inline mr-2" />
                  Loading…
                </td>
              </tr>
            )}
            {services?.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-8 text-center text-sm text-muted-foreground"
                >
                  No services engaged yet.{" "}
                  {canManage && (
                    <button onClick={openCreate} className="underline">
                      Add one
                    </button>
                  )}
                </td>
              </tr>
            )}
            {services?.map((s) => (
              <tr
                key={s.id}
                className="border-b border-border last:border-0 hover:bg-muted/40"
                data-testid={`service-row-${s.id}`}
              >
                <td className="px-3 py-2.5 font-medium">
                  {s.service_name || s.title}
                </td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground capitalize">
                  {(s.category || "—").replace(/_/g, " ")}
                </td>
                <td className="px-3 py-2.5 text-xs">
                  {s.assignee_id ? userName(s.assignee_id) : "—"}
                </td>
                <td className="px-3 py-2.5 text-xs">
                  <Badge variant="secondary" className="text-[10px] capitalize">
                    {s.recurrence === "none" ? "One-time" : s.recurrence}
                  </Badge>
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">
                  ₹{Number(s.default_bill_rate || 0).toLocaleString("en-IN")}
                </td>
                <td className="px-3 py-2.5 text-center">
                  {s.auto_invoice ? (
                    <Zap size={13} className="inline text-brand-gold" />
                  ) : (
                    <span className="text-muted-foreground text-xs">—</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  {canManage && (
                    <>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => openEdit(s)}
                        data-testid={`service-edit-${s.id}`}
                        title="Edit service"
                      >
                        <Pencil size={12} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-primary"
                        onClick={() => billNow(s)}
                        disabled={billingId === s.id}
                        data-testid={`service-bill-${s.id}`}
                        title="Bill now (draft invoice from rate)"
                      >
                        {billingId === s.id ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <Receipt size={12} />
                        )}
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-red-500"
                        onClick={() => stop(s)}
                        data-testid={`service-stop-${s.id}`}
                        title="Stop service"
                      >
                        <Trash2 size={12} />
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setEditingId(null);
        }}
      >
        <DialogContent className="max-w-lg" data-testid="new-service-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Edit service engagement" : "Add service engagement"}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? "Change rate, frequency, assignee or auto-invoice. Existing tasks and drafted invoices are not modified."
                : "Type any service the client is availing, set a rate + frequency, and CA OS will spawn a task and (optionally) auto-generate a draft invoice on completion."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Service name</Label>
              <Input
                value={form.service_name}
                onChange={(e) =>
                  setForm({ ...form, service_name: e.target.value })
                }
                placeholder="e.g. Monthly GSTR-3B, Bookkeeping, Payroll processing…"
                data-testid="svc-name"
                autoFocus
              />
              {!editingId && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {SERVICE_PRESETS.map((p) => (
                    <button
                      type="button"
                      key={p.name}
                      onClick={() =>
                        setForm({
                          ...form,
                          service_name: p.name,
                          category: p.category,
                        })
                      }
                      data-testid={`svc-preset-${p.name.replace(/\W+/g, "-").toLowerCase()}`}
                      className="text-[11px] px-2 py-1 rounded-full border border-border hover:bg-muted transition-colors"
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Category</Label>
              <Select
                value={form.category}
                onValueChange={(v) => setForm({ ...form, category: v })}
              >
                <SelectTrigger data-testid="svc-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.v} value={c.v}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Rate (₹ per task)</Label>
                <Input
                  type="number"
                  min={0}
                  step="1"
                  value={form.rate}
                  onChange={(e) => setForm({ ...form, rate: e.target.value })}
                  placeholder="e.g. 2500"
                  data-testid="svc-rate"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">GST rate (%)</Label>
                <Input
                  type="number"
                  min={0}
                  max={28}
                  value={form.gst_rate}
                  onChange={(e) =>
                    setForm({ ...form, gst_rate: e.target.value })
                  }
                  data-testid="svc-gst"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Frequency</Label>
                <Select
                  value={form.frequency}
                  onValueChange={(v) => setForm({ ...form, frequency: v })}
                >
                  <SelectTrigger data-testid="svc-freq">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((f) => (
                      <SelectItem key={f.v} value={f.v}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Assignee</Label>
                <Select
                  value={form.assignee_id}
                  onValueChange={(v) => setForm({ ...form, assignee_id: v })}
                >
                  <SelectTrigger data-testid="svc-assignee">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Unassigned —</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-md border border-border p-3 bg-muted/30">
              <div>
                <div className="text-sm font-medium">
                  Auto-generate invoice on completion
                </div>
                <div className="text-xs text-muted-foreground">
                  A draft invoice is created the moment the task moves to Done.
                </div>
              </div>
              <Switch
                checked={form.auto_invoice}
                onCheckedChange={(v) => setForm({ ...form, auto_invoice: v })}
                data-testid="svc-auto-invoice"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={busy || !form.rate || !form.service_name.trim()}
              data-testid="svc-submit"
            >
              {busy && <Loader2 size={12} className="animate-spin mr-1.5" />}
              {editingId ? "Save changes" : "Engage service"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Just-billed invoice opens in the same modal as the Invoices tab. */}
      {draftedId && (
        <InvoiceDetailDialog
          invoiceId={draftedId}
          clientName={client.name}
          onClose={() => setDraftedId(null)}
          onChange={onInvoiceChange}
        />
      )}
    </div>
  );
}
